import React, { useEffect, useState, useCallback } from "react"
import PropTypes from "prop-types"
import Chart from "react-apexcharts";
import FundsVerificationOverview from "./summary/FundsVerificationOverview";
import FundsVerificationAccountList from "./summary/FundsVerificationAccountList";
import axios from "axios";
import { formatTimestamp } from './utils'

const FundsVerificationSummary = ({ identifier, summary, pdf_host }) => {
  const [tab, setTab] = useState('overview')
  const lastSynced = summary.last_synced_at;
  const determinePreferredCurrency = () => {
    const preset = (summary.default_currency || '').toUpperCase();
    if (preset) return preset;
    const first = (summary.sources?.[0]?.currency || 'USD').toUpperCase();
    const allSame = summary.sources.every(s => (s.currency || first) === first);
    return allSame ? first : (first || 'USD');
  };

  const [currency, setCurrency] = useState(determinePreferredCurrency());
  const [currencySearch, setCurrencySearch] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [showError, setShowError] = useState(false);
  const [showPdfTip, setShowPdfTip] = useState(false);

  const handleCurrencySearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      const typed = currencySearch.trim().toUpperCase();
      if (typed.length) setCurrency(typed);
    }
  };

  const defaultCurrency = React.useMemo(() => determinePreferredCurrency().toUpperCase(), [summary]);
  const currencyCodes = React.useMemo(() => {
    const list = summary.currency_codes || [];
    const merged = new Set(list.map(c => c.toUpperCase()));
    merged.add(defaultCurrency);
    return Array.from(merged);
  }, [summary, defaultCurrency]);

  const filteredCodes = React.useMemo(() => {
    const term = currencySearch.trim().toUpperCase();
    const current = (currency || 'USD').toUpperCase();

    const pushUnique = (arr, code) => {
      if (code && !arr.includes(code)) arr.push(code);
    };

    const result = [];
    // Always show default on top
    pushUnique(result, defaultCurrency);

    if (term.length === 0) {
      // No search text: show selected currency (if different) under default
      if (current !== defaultCurrency) pushUnique(result, current);
      return result;
    }

    const matches = currencyCodes.filter((code) => code.includes(term));
    matches.forEach((code) => pushUnique(result, code));
    return result;
  }, [currencySearch, currencyCodes, currency, defaultCurrency]);

  useEffect(() => {
    setCurrency(determinePreferredCurrency());
  }, [summary]);

  const setCurrentCurrency = (e) => {
    setCurrency(e.target.dataset.type)
  }
  const downloadPdf = (e) => {
    setDownloading(true);
    setErrorMsg(null);
    axios(`${pdf_host}/summary_pdf/${identifier}?currency=${encodeURIComponent(currency || '')}`, {
      method: 'GET',
      responseType: 'blob'
    }).then(({ data }) => {
      const file = new Blob([data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      setDownloading(false);
      let alink = document.createElement('a');
      alink.href = fileURL;
      alink.download = 'fundsv-summary.pdf';
      alink.click();
    }).catch(({ response }) => {
      setDownloading(false);
      console.log(response.data?.error);
      setErrorMsg('An error occurred. Please try again.')
      setShowError(true);
    })
  }

  const allZero = summary.sources.every((source) => {
    return Object.values(source.last_6_months).every((amount) => amount == 0)
  });

  const closeErrorModal = () => {
    setShowError(false);
    setErrorMsg(null);
  }

  return (
    <>
      <div className={`modal ${showError ? 'is-active' : ''}`}>
        <div className="modal-background" onClick={closeErrorModal}></div>
        <div className="modal-card">
          <header className="modal-card-head">
            <p className="modal-card-title">Error</p>
            <button className="delete" aria-label="close" onClick={closeErrorModal}></button>
          </header>
          <section className="modal-card-body">
            {errorMsg}
          </section>
          <footer className="modal-card-foot">
            <button className="button is-danger" onClick={closeErrorModal}>Close</button>
          </footer>
        </div>
      </div>

      <div className="funds-verification-overview">
        <div
          className="columns is-variable is-2"
        >
          <div className="identifier column is-two-thirds pr-4"
            style={{ paddingRight: "10px", paddingLeft: "10px"}}
          >
            <div style={{ backgroundColor: "#0b0047", color: "white", padding: "10px", borderRadius: "5px", display: "flex", flexWrap: "wrap", gap: "1rem" }}>
              <div className="m-1"><b>Identifier:</b> {summary.external_identifier}</div>
            </div>
          </div>
          <div
            className="summary-tab-list column is-one-third pl-4"
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}
          >
            <div style={{ flex: 1}}></div>
            <div className="download-pdf" style={{ position: 'relative', overflow: 'visible' }}>
              <button
                className="button"
                aria-haspopup="true"
                disabled={downloading}
                onClick={downloadPdf}
                data-turbolinks='false'
                onMouseEnter={() => setShowPdfTip(true)}
                onMouseLeave={() => setShowPdfTip(false)}
              >
                <span style={{ fontSize: '0.95rem' }}>{downloading ? 'Downloading...' : 'PDF'}</span>
              </button>
              <span
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '-38px',
                  background: '#363636',
                  color: '#fff',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                  opacity: showPdfTip ? 1 : 0,
                  transition: 'opacity 0.05s ease-in-out',
                  pointerEvents: 'none',
                  zIndex: 30
                }}
              >
                For a currency-specific PDF, please use your device&apos;s "Print-to-PDF" function.
              </span>
            </div>
            
            <div className="currency-displays">
              <div className="dropdown is-hoverable">
                <div className="dropdown-trigger">
                  <button className="button" aria-haspopup="true" aria-controls="dropdown-menu4" style={{ paddingRight: '14px' }}>
                    <span style={{ fontSize: '0.95rem' }}>Currency</span>
                    <span className="icon is-small">
                      <i className="fas fa-angle-down" aria-hidden="true"></i>
                    </span>
                  </button>
                </div>
                <div
                  className="dropdown-menu"
                  id="dropdown-menu4"
                  role="menu"
                  style={{
                    left: 'auto',
                    right: 0,
                    minWidth: '12rem',
                    paddingTop: '4px',
                    position: 'absolute',
                    top: '100%',
                    zIndex: 20
                  }}
                >
                  <div className="dropdown-content" style={{ paddingRight: '18px' }}>
                    <div style={{ padding: '8px' }}>
                      <input
                        className="input is-small"
                        type="text"
                        placeholder="Search currency"
                        value={currencySearch}
                        onChange={(e) => setCurrencySearch(e.target.value)}
                        onKeyDown={handleCurrencySearchKeyDown}
                      />
                    </div>
                    {filteredCodes.map((code) => (
                      <a
                        key={code}
                        data-type={code}
                        onClick={setCurrentCurrency}
                        className={"dropdown-item " + (currency === code ? 'is-active' : '')}
                      >
                        {code}
                      </a>
                    ))}
                    {currencySearch.trim() !== '' && filteredCodes.length === 0 && (
                      <div className="dropdown-item" style={{ color: '#999' }}>No matches</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <FundsVerificationOverview summary={summary} currency={currency} allZero={allZero}/>
      </div>
    </>
  );
}


FundsVerificationSummary.propTypes = {
  identifier: PropTypes.string,
  summary: PropTypes.object
};
export default FundsVerificationSummary
